<template>
  <div>
    <header-component />
    <div class="px-4 py-4">

      <!-- Tab switcher -->
      <div class="flex gap-2 mb-5">
        <button
          @click="activeTab = 'daily'"
          :class="activeTab === 'daily'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'"
          class="px-5 py-2 rounded-lg text-sm font-medium transition"
        >Daily View</button>
        <button
          @click="activeTab = 'monthly'"
          :class="activeTab === 'monthly'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'"
          class="px-5 py-2 rounded-lg text-sm font-medium transition"
        >Monthly View</button>
      </div>

      <!-- ===================== DAILY VIEW ===================== -->
      <div v-if="activeTab === 'daily'">

      <!-- Filters bar -->
      <div class="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <label class="block text-xs text-gray-500 mb-1">Date</label>
          <input
            type="date"
            v-model="filters.date"
            @change="fetchRecords"
            class="border border-gray-300 rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label class="block text-xs text-gray-500 mb-1">Branch</label>
          <select
            v-model="filters.branchId"
            @change="fetchRecords"
            class="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="">All Branches</option>
            <option v-for="branch in branches" :key="branch.id" :value="branch.id">
              {{ branch.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-xs text-gray-500 mb-1">Type</label>
          <select
            v-model="filters.attendeeType"
            @change="fetchRecords"
            class="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="vendor">Vendor</option>
            <option value="user">User</option>
          </select>
        </div>

        <div>
          <base-button size="sm" type="primary" @click="openModal">Mark Attendance</base-button>
        </div>
      </div>

      <!-- Summary chips -->
      <div class="flex flex-wrap gap-2 mb-4">
        <span class="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
          Present: {{ summary.totalPresent }}
        </span>
        <span class="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
          Absent: {{ summary.totalAbsent }}
        </span>
        <span class="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
          Vendors: {{ summary.vendorPresent }}P / {{ summary.vendorAbsent }}A
        </span>
        <span class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
          Users: {{ summary.userPresent }}P / {{ summary.userAbsent }}A
        </span>
      </div>

      <!-- Loading spinner -->
      <div v-if="loading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>

      <!-- Table -->
      <div v-else class="overflow-x-auto">
        <table class="min-w-full bg-white border border-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date &amp; Time</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marked By</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Selfie</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="records.length === 0">
              <td colspan="8" class="text-center py-12 text-gray-400">No attendance records for this date.</td>
            </tr>
            <tr
              v-for="record in records"
              :key="record.id"
              class="hover:bg-gray-50 border-b"
            >
              <!-- Name -->
              <td class="px-4 py-3 text-sm text-gray-800">{{ record.attendeeName }}</td>

              <!-- Type badge -->
              <td class="px-4 py-3">
                <span
                  :class="record.attendeeType === 'vendor' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'"
                  class="px-2 py-0.5 rounded text-xs font-medium capitalize"
                >
                  {{ record.attendeeType }}
                </span>
              </td>

              <!-- Branch -->
              <td class="px-4 py-3 text-sm text-gray-700">{{ record.branchName || '—' }}</td>

              <!-- Date & Time -->
              <td class="px-4 py-3 text-sm text-gray-700">{{ formatDateTime(record.attendedAt) }}</td>

              <!-- Status -->
              <td class="px-4 py-3">
                <span
                  v-if="record.isPresent"
                  class="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium"
                >Present</span>
                <span
                  v-else
                  class="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium"
                >Absent</span>
              </td>

              <!-- Marked By -->
              <td class="px-4 py-3 text-sm text-gray-700">
                <span v-if="record.markedBy === 'self'">Self</span>
                <span v-else>Manager: {{ record.managerName }}</span>
              </td>

              <!-- Reason -->
              <td class="px-4 py-3 text-sm text-gray-700">
                <span :title="record.reason">{{ truncate(record.reason, 60) }}</span>
              </td>

              <!-- Selfie -->
              <td class="px-4 py-3">
                <img
                  v-if="record.photoUrl"
                  :src="record.photoUrl"
                  @click="openLightbox(record.photoUrl)"
                  class="w-10 h-10 rounded-full object-cover cursor-pointer"
                  alt="Selfie"
                />
                <span v-else class="text-gray-400">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      </div>
      <!-- ===================== END DAILY VIEW ===================== -->

      <!-- ===================== MONTHLY VIEW ===================== -->
      <div v-if="activeTab === 'monthly'">

        <!-- Monthly filters -->
        <div class="flex flex-wrap gap-3 mb-5 items-end">
          <div>
            <label class="block text-xs text-gray-500 mb-1">Type</label>
            <div class="flex gap-3">
              <label class="flex items-center gap-1 text-sm cursor-pointer">
                <input type="radio" v-model="monthly.attendeeType" value="vendor" @change="onMonthlyTypeChange" /> Vendor
              </label>
              <label class="flex items-center gap-1 text-sm cursor-pointer">
                <input type="radio" v-model="monthly.attendeeType" value="user" @change="onMonthlyTypeChange" /> User
              </label>
            </div>
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">Attendee</label>
            <select
              v-model="monthly.attendeeId"
              @change="fetchMonthly"
              class="border border-gray-300 rounded px-3 py-2 text-sm min-w-48"
            >
              <option value="">Select attendee...</option>
              <option v-for="a in monthly.filteredAttendees" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">Month</label>
            <input
              type="month"
              v-model="monthly.month"
              @change="fetchMonthly"
              class="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
        </div>

        <!-- Loading -->
        <div v-if="monthly.loading" class="flex justify-center py-12">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>

        <div v-else-if="monthly.days.length">
          <!-- Attendee name + summary chips -->
          <div class="flex flex-wrap items-center gap-3 mb-4">
            <h3 class="text-base font-semibold text-gray-800">{{ monthly.attendeeName }}</h3>
            <span class="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
              Present: {{ monthly.summary.present }}
            </span>
            <span class="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
              Absent: {{ monthly.summary.absent }}
            </span>
            <span class="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-medium">
              Upcoming: {{ monthly.days.filter(d => d.status === 'future').length }}
            </span>
          </div>

          <!-- Calendar grid -->
          <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <!-- Day-of-week header -->
            <div class="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
              <div v-for="d in ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']" :key="d"
                class="py-2 text-center text-xs font-semibold text-gray-500 uppercase">
                {{ d }}
              </div>
            </div>

            <!-- Calendar body -->
            <div class="grid grid-cols-7">
              <!-- Leading blank cells for first week -->
              <div
                v-for="n in monthly.days[0].dayOfWeek"
                :key="'blank-' + n"
                class="border-b border-r border-gray-100 p-1 min-h-16"
              ></div>

              <!-- Day cells -->
              <div
                v-for="day in monthly.days"
                :key="day.date"
                class="border-b border-r border-gray-100 p-2 min-h-16 flex flex-col items-center justify-start gap-1"
              >
                <span class="text-xs text-gray-400 self-start">{{ day.day }}</span>
                <span
                  v-if="day.status === 'present'"
                  class="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-bold"
                >P</span>
                <span
                  v-else-if="day.status === 'absent'"
                  class="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-bold"
                >A</span>
                <!-- future: empty cell -->
              </div>
            </div>
          </div>

          <!-- Legend -->
          <div class="flex gap-4 mt-3 text-xs text-gray-500">
            <span class="flex items-center gap-1">
              <span class="w-5 h-5 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold text-xs">P</span>
              Present
            </span>
            <span class="flex items-center gap-1">
              <span class="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-xs">A</span>
              Absent
            </span>
            <span class="flex items-center gap-1">
              <span class="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs">—</span>
              Upcoming
            </span>
          </div>
        </div>

        <div v-else-if="!monthly.loading && monthly.attendeeId" class="text-center py-12 text-gray-400">
          No records found for this month.
        </div>
        <div v-else-if="!monthly.attendeeId" class="text-center py-12 text-gray-400">
          Select an attendee to view their monthly attendance.
        </div>

      </div>
      <!-- ===================== END MONTHLY VIEW ===================== -->

      <!-- Manager mark modal -->
      <div
        v-if="showMarkModal"
        class="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4"
      >
        <div class="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-bold text-gray-800">Mark Attendance</h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600">&#x2715;</button>
          </div>

          <!-- Type radio -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <div class="flex gap-4">
              <label class="flex items-center gap-1 text-sm cursor-pointer">
                <input type="radio" v-model="markForm.attendeeType" value="vendor" @change="onTypeChange" />
                Vendor
              </label>
              <label class="flex items-center gap-1 text-sm cursor-pointer">
                <input type="radio" v-model="markForm.attendeeType" value="user" @change="onTypeChange" />
                User
              </label>
            </div>
          </div>

          <!-- Attendee select -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Attendee</label>
            <select
              v-model="markForm.attendeeId"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Select attendee...</option>
              <option v-for="attendee in filteredAttendees" :key="attendee.id" :value="attendee.id">
                {{ attendee.name }}
              </option>
            </select>
            <p v-if="markErrors.attendeeId" class="text-red-500 text-xs mt-1">{{ markErrors.attendeeId }}</p>
          </div>

          <!-- Date & Time -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Date &amp; Time</label>
            <input
              type="datetime-local"
              v-model="markForm.attendedAt"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <p v-if="markErrors.attendedAt" class="text-red-500 text-xs mt-1">{{ markErrors.attendedAt }}</p>
          </div>

          <!-- Reason -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <textarea
              v-model="markForm.reason"
              rows="3"
              placeholder="Reason (required, min 10 chars)"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
            ></textarea>
            <p v-if="markErrors.reason" class="text-red-500 text-xs mt-1">{{ markErrors.reason }}</p>
          </div>

          <div class="flex gap-3 mt-6">
            <base-button type="secondary" @click="closeModal" class="flex-1">Cancel</base-button>
            <base-button type="primary" @click="submitMark" :disabled="markLoading" class="flex-1">
              <span v-if="markLoading">...</span>
              <span v-else>Mark</span>
            </base-button>
          </div>
        </div>
      </div>

      <!-- Selfie lightbox -->
      <div
        v-if="lightboxUrl"
        class="fixed inset-0 bg-black bg-opacity-80 z-60 flex items-center justify-center"
        @click="lightboxUrl = null"
      >
        <img
          :src="lightboxUrl"
          class="max-w-full max-h-full rounded-lg"
          alt="Selfie"
          @click.stop
        />
        <button
          class="absolute top-4 right-4 text-white text-2xl"
          @click="lightboxUrl = null"
        >&#x2715;</button>
      </div>

    </div>
  </div>
</template>

<script>
import Api from "@/middleware/api";
import endpoints from "@/utils/constants";

export default {
  name: "AttendanceDashboard",
  data() {
    return {
      records: [],
      summary: { totalPresent: 0, totalAbsent: 0, vendorPresent: 0, userPresent: 0, vendorAbsent: 0, userAbsent: 0 },
      branches: [],
      filters: {
        date: new Date().toISOString().split("T")[0],
        branchId: "",
        attendeeType: "",
      },
      activeTab: "daily",
      loading: false,
      showMarkModal: false,
      monthly: {
        attendeeType: "vendor",
        attendeeId: "",
        month: new Date().toISOString().slice(0, 7),
        attendees: [],
        filteredAttendees: [],
        loading: false,
        days: [],
        attendeeName: "",
        summary: { present: 0, absent: 0 },
      },
      markForm: {
        attendeeType: "vendor",
        attendeeId: "",
        attendedAt: "",
        reason: "",
      },
      attendees: [],
      filteredAttendees: [],
      markLoading: false,
      markErrors: {},
      lightboxUrl: null,
    };
  },
  created() {
    this.fetchRecords();
    this.fetchMonthlyAttendees();
  },
  methods: {
    async fetchRecords() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        params.set("date", this.filters.date);
        if (this.filters.branchId) params.set("branch_id", this.filters.branchId);
        if (this.filters.attendeeType) params.set("attendee_type", this.filters.attendeeType);
        const url = `${endpoints.LARAVEL_BASE_URL}/api/v1/attendance?${params}`;
        const res = await new Api().call("get", url);
        this.records = res.data.data.map(r => ({
          id:           r.id,
          attendeeName: r.attendee_name,
          attendeeType: r.attendee_type,
          branchName:   r.branch_name,
          attendedAt:   r.attended_at,
          isPresent:    r.is_present,
          markedBy:     r.marked_by,
          managerName:  r.manager_name,
          reason:       r.reason,
          photoUrl:     r.photo_url ? `${endpoints.LARAVEL_BASE_URL}${r.photo_url}` : null,
        }));
        this.summary = res.data.summary;
        this.branches = res.data.branches;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async fetchAttendees() {
      try {
        const res = await new Api().call(
          "get",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/attendance/attendees`
        );
        this.attendees = res.data.attendees;
        this.onTypeChange();
      } catch (e) {
        console.error(e);
      }
    },

    openModal() {
      this.showMarkModal = true;
      this.fetchAttendees();
    },

    closeModal() {
      this.showMarkModal = false;
      this.markForm = {
        attendeeType: "vendor",
        attendeeId: "",
        attendedAt: "",
        reason: "",
      };
      this.markErrors = {};
    },

    onTypeChange() {
      this.markForm.attendeeId = "";
      this.filteredAttendees = this.attendees.filter(
        (a) => a.type === this.markForm.attendeeType
      );
    },

    async submitMark() {
      this.markErrors = {};
      if (!this.markForm.attendeeId) {
        this.markErrors.attendeeId = "Select an attendee";
      }
      if (!this.markForm.attendedAt) {
        this.markErrors.attendedAt = "Date & time required";
      }
      if (!this.markForm.reason || this.markForm.reason.length < 10) {
        this.markErrors.reason = "Reason must be at least 10 characters";
      }
      if (Object.keys(this.markErrors).length) return;

      this.markLoading = true;
      try {
        await new Api().call(
          "post",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/attendance/manager`,
          {
            attendee_type: this.markForm.attendeeType,
            attendee_id: this.markForm.attendeeId,
            attended_at: new Date(this.markForm.attendedAt).toISOString(),
            reason: this.markForm.reason,
          }
        );
        this.closeModal();
        this.fetchRecords();
        window.notify({
          message: "Attendance marked successfully",
          type: "success",
          title: "Done",
        });
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          "Failed to mark attendance";
        window.notify({ message: msg, type: "danger", title: "Error" });
      } finally {
        this.markLoading = false;
      }
    },

    async fetchMonthlyAttendees() {
      try {
        const res = await new Api().call("get", `${endpoints.LARAVEL_BASE_URL}/api/v1/attendance/attendees`);
        this.monthly.attendees = res.data.attendees;
        this.onMonthlyTypeChange();
      } catch (e) {
        console.error(e);
      }
    },

    onMonthlyTypeChange() {
      this.monthly.attendeeId = "";
      this.monthly.days = [];
      this.monthly.filteredAttendees = this.monthly.attendees.filter(
        (a) => a.type === this.monthly.attendeeType
      );
    },

    async fetchMonthly() {
      if (!this.monthly.attendeeId) return;
      this.monthly.loading = true;
      this.monthly.days = [];
      try {
        const params = new URLSearchParams({
          attendee_type: this.monthly.attendeeType,
          attendee_id:   this.monthly.attendeeId,
          month:         this.monthly.month,
        });
        const res = await new Api().call(
          "get",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/attendance/monthly?${params}`
        );
        this.monthly.days         = res.data.days;
        this.monthly.attendeeName = res.data.attendeeName;
        this.monthly.summary      = res.data.summary;
      } catch (e) {
        console.error(e);
      } finally {
        this.monthly.loading = false;
      }
    },

    openLightbox(url) {
      this.lightboxUrl = url;
    },

    formatDateTime(val) {
      if (!val) return "—";
      // API returns UTC strings without a timezone suffix — append Z to force correct parsing
      const d = /Z|[+-]\d{2}:\d{2}$/.test(val) ? new Date(val) : new Date(val + "Z");
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    truncate(str, len) {
      if (!str) return "—";
      return str.length > len ? str.slice(0, len) + "..." : str;
    },
  },
};
</script>
